import { SpacesSchema, type Spaces, type Space } from './spaces.js';
import type { Board } from './board.js';
import { MAX_SPACES } from './constants.js';

/**
 * ============================================================================
 * IMMUTABLE SPACES-EDIT OPERATIONS
 * ============================================================================
 * The ONLY sanctioned way the app mutates a Spaces envelope (add / rename / delete
 * / duplicate a space, switch the active one, commit a board edit into a space,
 * reorder). The Spaces analogue of board-ops.ts — SAME five guarantees:
 *
 *   PURE            — no ambient state, deterministic.
 *   IMMUTABLE       — never mutates its input; returns a brand-new Spaces (or error).
 *   TOTAL           — never throws; every failure is a typed `SpacesOpError` result.
 *   SELF-VALIDATING — every successful result is re-parsed through `SpacesSchema`, so
 *                     an op can NEVER produce an invalid envelope. If a change would
 *                     violate the schema (dup id/name, dangling active pointer, an
 *                     invalid nested board), the op returns a typed error, never an
 *                     invalid Spaces.
 *   ID-AGNOSTIC     — ids are generated by the APP layer (like tile ids via
 *                     generateTileId + addTile). Ops that need a NEW id RECEIVE it
 *                     from the caller (createSpace takes a fully-formed Space;
 *                     duplicateSpace takes the new space id AND the new board id).
 *                     The schema/ops layer never mints ids.
 *
 * THE BRIDGE (updateSpaceBoard): every existing board-op commit funnels through here.
 * The app runs a board-op (addTile/moveTile/…) on the ACTIVE space's board, gets a
 * new Board back, then calls updateSpaceBoard(spaces, activeSpaceId, newBoard) to fold
 * it into the envelope. Board-ops stay 100% unchanged and unaware of Spaces.
 *
 * DELETE / ACTIVE-REASSIGN (deterministic, documented): deleteSpace rejects deleting
 * the LAST remaining space (`last-space`). If the deleted space was the active one,
 * activeSpaceId moves to the FIRST space in the POST-DELETION order — a deterministic,
 * stable choice (never a random or "nearest" pick).
 *
 * DUPLICATE SCOPE (documented): duplicateSpace deep-copies the source board and swaps
 * in the caller's new board.id, but KEEPS the tile ids as-is. Tile-id uniqueness is
 * PER BOARD, never across the envelope — two spaces holding `tile_clock1` is fine and
 * intended (each board is its own id namespace). The default name is "<source> copy",
 * deduped so the unique-name invariant holds.
 * ============================================================================
 */

/** Typed failure reasons — a closed set so callers can switch exhaustively. */
export type SpacesOpError =
  /** No space with the given id in the envelope (rename/delete/duplicate/setActive/update). */
  | 'unknown-space'
  /** Cannot delete the last remaining space — a user always has at least one. */
  | 'last-space'
  /** Adding/duplicating would exceed MAX_SPACES. */
  | 'max-spaces'
  /** New/renamed name collides with another space (case-insensitive). */
  | 'duplicate-name'
  /** Create/duplicate target space id already exists in the envelope. */
  | 'id-collision'
  /** The resulting envelope failed SpacesSchema (bad shape, invalid nested board, …). */
  | 'invalid';

/** Discriminated result: a new validated Spaces, or a typed error. Never an exception. */
export type SpacesOpResult =
  | { readonly ok: true; readonly spaces: Spaces }
  | { readonly ok: false; readonly error: SpacesOpError; readonly detail?: string };

const ok = (spaces: Spaces): SpacesOpResult => ({ ok: true, spaces });
const err = (error: SpacesOpError, detail?: string): SpacesOpResult =>
  detail === undefined ? { ok: false, error } : { ok: false, error, detail };

/**
 * Re-validate a candidate envelope through SpacesSchema and wrap the outcome. The
 * single choke point that makes "an op can never produce an invalid envelope" true:
 * EVERY op ends here. A parse failure becomes `invalid` (unless a more specific error
 * was returned earlier), never a thrown error or a leaked malformed Spaces.
 */
function validated(candidate: unknown): SpacesOpResult {
  const parsed = SpacesSchema.safeParse(candidate);
  return parsed.success ? ok(parsed.data) : err('invalid', parsed.error.message);
}

/** Find a space by id, or undefined. */
function findSpace(spaces: Spaces, spaceId: string): Space | undefined {
  return spaces.spaces.find((s) => s.id === spaceId);
}

/** Is `name` already used by ANOTHER space (case-insensitive)? `exceptId` excludes self. */
function nameTaken(spaces: Spaces, name: string, exceptId?: string): boolean {
  const key = name.trim().toLowerCase();
  return spaces.spaces.some((s) => s.id !== exceptId && s.name.toLowerCase() === key);
}

/**
 * Add a fully-formed space (caller generated its id + built its board — mirrors
 * addTile). Fails on id collision, duplicate name (case-insensitive), the MAX_SPACES
 * cap, or a structurally invalid space (→ `invalid`). activeSpaceId is UNCHANGED:
 * creating a space does not steal focus (the app switches explicitly via setActiveSpace).
 */
export function createSpace(spaces: Spaces, space: Space): SpacesOpResult {
  if (findSpace(spaces, space.id)) {
    return err('id-collision', `space id "${space.id}" already exists`);
  }
  // Specific caps BEFORE the generic schema parse so callers get an actionable error.
  if (spaces.spaces.length >= MAX_SPACES) {
    return err('max-spaces', `already at the ${MAX_SPACES}-space cap`);
  }
  // Name check reads the incoming name defensively (it may be untrimmed garbage; the
  // schema parse below is the real guard for shape, this is the friendly-error path).
  if (typeof space.name === 'string' && nameTaken(spaces, space.name)) {
    return err('duplicate-name', `a space named "${space.name}" already exists`);
  }
  const candidate: Spaces = { ...spaces, spaces: [...spaces.spaces, space] };
  return validated(candidate);
}

/**
 * Rename an existing space. Fails on unknown id, a case-insensitive collision with a
 * DIFFERENT space (`duplicate-name`), or an invalid new name (empty/untrimmed/too
 * long → `invalid`, caught by the schema). Renaming a space to its own current name —
 * including a pure case change (Work → WORK) — is allowed (exceptId excludes self).
 */
export function renameSpace(spaces: Spaces, spaceId: string, newName: string): SpacesOpResult {
  if (!findSpace(spaces, spaceId)) {
    return err('unknown-space', `no space "${spaceId}"`);
  }
  if (typeof newName === 'string' && nameTaken(spaces, newName, spaceId)) {
    return err('duplicate-name', `a space named "${newName}" already exists`);
  }
  const candidate: Spaces = {
    ...spaces,
    spaces: spaces.spaces.map((s) => (s.id === spaceId ? { ...s, name: newName } : s)),
  };
  return validated(candidate);
}

/**
 * Delete a space. Rejects deleting the LAST remaining space (`last-space`). If the
 * deleted space was active, activeSpaceId moves to the FIRST space in the post-deletion
 * order (deterministic). Fails on unknown id.
 */
export function deleteSpace(spaces: Spaces, spaceId: string): SpacesOpResult {
  if (!findSpace(spaces, spaceId)) {
    return err('unknown-space', `no space "${spaceId}"`);
  }
  if (spaces.spaces.length <= 1) {
    return err('last-space', 'cannot delete the last remaining space');
  }
  const remaining = spaces.spaces.filter((s) => s.id !== spaceId);
  // Deterministic active-reassign: if we removed the active one, fall to the first
  // remaining (remaining is guaranteed non-empty here — length was >= 2).
  const nextActive =
    spaces.activeSpaceId === spaceId ? remaining[0]!.id : spaces.activeSpaceId;
  const candidate: Spaces = { ...spaces, spaces: remaining, activeSpaceId: nextActive };
  return validated(candidate);
}

/**
 * Duplicate a space. Deep-copies the source board, swaps in the caller-provided
 * `newBoardId` (board id is regenerated — a copy is a NEW board), and KEEPS the source
 * tile ids (uniqueness is per-board scope). The new space gets `newSpaceId` and a
 * default deduped name "<source> copy". Fails on unknown source, id collision, or the
 * MAX_SPACES cap. activeSpaceId is UNCHANGED (the app switches explicitly if desired).
 */
export function duplicateSpace(
  spaces: Spaces,
  sourceSpaceId: string,
  newSpaceId: string,
  newBoardId: string,
): SpacesOpResult {
  const source = findSpace(spaces, sourceSpaceId);
  if (!source) {
    return err('unknown-space', `no space "${sourceSpaceId}"`);
  }
  if (findSpace(spaces, newSpaceId)) {
    return err('id-collision', `space id "${newSpaceId}" already exists`);
  }
  if (spaces.spaces.length >= MAX_SPACES) {
    return err('max-spaces', `already at the ${MAX_SPACES}-space cap`);
  }

  // Deep copy the board (structuredClone-free: these are plain JSON values), then swap
  // the board id. This keeps the source board fully untouched (immutability).
  const copiedBoard = deepCopyBoard(source.board, newBoardId);

  // Default name, deduped case-insensitively so the unique-name invariant holds.
  const dupName = uniqueCopyName(spaces, source.name);

  // `copiedBoard` is `unknown` (a fresh JSON clone), so the candidate is untyped until
  // it clears the `validated()` choke-point — the same "candidate is opaque pre-parse"
  // discipline board-ops uses. SpacesSchema re-validates the whole nested board anyway.
  const newSpace = { id: newSpaceId, name: dupName, board: copiedBoard };
  const candidate: unknown = { ...spaces, spaces: [...spaces.spaces, newSpace] };
  return validated(candidate);
}

/** Deep-copy a board's plain-JSON value and override its id. Pure; never mutates input. */
function deepCopyBoard(board: Board, newBoardId: string): unknown {
  const copy = JSON.parse(JSON.stringify(board)) as Record<string, unknown>;
  copy.id = newBoardId;
  return copy;
}

/**
 * Produce a unique (case-insensitive) name for a duplicate. Tries "<base> copy" first,
 * then "<base> copy 2", "…3", … until free. Truncates the base if needed so the result
 * stays within the name-length cap (the schema is still the final guard).
 */
function uniqueCopyName(spaces: Spaces, base: string): string {
  const attempt = (suffix: string): string => `${base} copy${suffix}`;
  if (!nameTaken(spaces, attempt(''))) return attempt('');
  for (let n = 2; n < 1000; n += 1) {
    const candidate = attempt(` ${n}`);
    if (!nameTaken(spaces, candidate)) return candidate;
  }
  // Extremely unlikely fallthrough — return the base attempt and let the schema decide.
  return attempt('');
}

/**
 * Switch the active space. Fails on unknown id. Setting the already-active space is a
 * valid no-op-equivalent (returns a new, equal envelope).
 */
export function setActiveSpace(spaces: Spaces, spaceId: string): SpacesOpResult {
  if (!findSpace(spaces, spaceId)) {
    return err('unknown-space', `no space "${spaceId}"`);
  }
  const candidate: Spaces = { ...spaces, activeSpaceId: spaceId };
  return validated(candidate);
}

/**
 * THE BRIDGE: replace one space's board with a new (already board-op-validated) Board.
 * This is how every board-op commit reaches the envelope — the app runs a board-op on
 * the active space's board, then folds the result back in here. Fails on unknown space
 * or an invalid board payload (→ `invalid`). Other spaces and activeSpaceId untouched.
 */
export function updateSpaceBoard(spaces: Spaces, spaceId: string, board: Board): SpacesOpResult {
  if (!findSpace(spaces, spaceId)) {
    return err('unknown-space', `no space "${spaceId}"`);
  }
  const candidate: Spaces = {
    ...spaces,
    spaces: spaces.spaces.map((s) => (s.id === spaceId ? { ...s, board } : s)),
  };
  return validated(candidate);
}

/**
 * Reorder spaces to match `orderedIds` (a permutation of the current space ids). This
 * is the page-order for the iOS-style picker. `orderedIds` must be EXACTLY the current
 * set — same length, no unknowns, no duplicates — or the op fails. activeSpaceId is a
 * stable id (not an index), so it is unaffected by reordering.
 *
 * Errors:
 *   - `invalid`       — wrong length, or a duplicate id (not a permutation).
 *   - `unknown-space` — an id in orderedIds names no current space.
 */
export function reorderSpaces(spaces: Spaces, orderedIds: readonly string[]): SpacesOpResult {
  if (orderedIds.length !== spaces.spaces.length) {
    return err(
      'invalid',
      `orderedIds length ${orderedIds.length} !== ${spaces.spaces.length} spaces`,
    );
  }
  const seen = new Set<string>();
  for (const id of orderedIds) {
    if (seen.has(id)) {
      return err('invalid', `duplicate id "${id}" in orderedIds (not a permutation)`);
    }
    seen.add(id);
    if (!findSpace(spaces, id)) {
      return err('unknown-space', `no space "${id}"`);
    }
  }
  // Every id is known and unique, and counts match → it is a genuine permutation.
  const byId = new Map(spaces.spaces.map((s) => [s.id, s] as const));
  const reordered = orderedIds.map((id) => byId.get(id)!);
  const candidate: Spaces = { ...spaces, spaces: reordered };
  return validated(candidate);
}
